import {
    CashFlowConfig,
    CashFlowConfigSaveRequest,
    CashFlowType,
    DataType,
    FactorDateFrame,
    FrequencyType,
} from '@ndvr/portfolio-api';

import { convertFormDateToServerDate, convertServerDateToFormDate } from 'utils/date-formatter';
import { CashflowStream } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/EditCashflowDialog';

const frequencyToFrequencyType = {
    'one-time': FrequencyType.ONE_TIME,
    'every-week': FrequencyType.WEEKLY,
    'every-2-weeks': FrequencyType.BI_WEEKLY,
    'every-month': FrequencyType.MONTHLY,
    'every-3-months': FrequencyType.QUARTERLY,
    'every-6-months': FrequencyType.SEMI_ANNUALLY,
    'every-year': FrequencyType.ANNUALLY,
};

export const convertFormToCashFlowConfigSaveRequest = (
    globalAccountId: string,
    form: CashflowStream,
): CashFlowConfigSaveRequest => {
    const factorDateFrame: FactorDateFrame = {
        frequencyType: frequencyToFrequencyType[form.frequency],
        startDate: convertFormDateToServerDate(form.startDate),
    };
    if (form.frequency !== 'one-time' && form.repeatUntil === 'specific-date') {
        factorDateFrame.endDate = convertFormDateToServerDate(form.repeatEndDate!);
    }
    if (form.frequency !== 'one-time' && form.repeatUntil === 'occurrences') {
        factorDateFrame.occurrenceCount = form.repeatOccurrences;
    }

    const result: CashFlowConfigSaveRequest = {
        title: {
            plainText: form.name,
            dataType: DataType.SI_FACTOR_TITLE,
            encryptionContextGUID: globalAccountId,
        },
        cashFlowType: form.type === 'contribution' ? CashFlowType.CONTRIBUTION : CashFlowType.WITHDRAWAL,
        amount: form.amount.toString(),
        factorDateFrame,
        inflationConfig: {
            inflationAdjusted: form.adjustForInflation,
            inflationPercent: form.inflationRate.toString(),
            inflationStartDate: convertFormDateToServerDate(form.startDate),
        },
    };

    if (form.account !== 'portfolio_level') {
        result.accountGUID = form.account;
    }

    return result;
};

export const convertCashFlowConfigToForm = (cashFlowConfig: CashFlowConfig): CashflowStream => {
    const frequencyType = cashFlowConfig.factorDateFrame.frequencyType;
    const frequency = (Object.keys(frequencyToFrequencyType) as Array<keyof typeof frequencyToFrequencyType>).find(
        (key) => frequencyToFrequencyType[key] === frequencyType,
    );

    const result: CashflowStream = {
        id: cashFlowConfig.cashFlowConfigGUID,
        name: cashFlowConfig.title.plainText,
        type: cashFlowConfig.cashFlowType === CashFlowType.CONTRIBUTION ? 'contribution' : 'withdrawal',
        frequency: frequency || 'one-time',
        amount: parseFloat(cashFlowConfig.amount),
        startDate: convertServerDateToFormDate(cashFlowConfig.factorDateFrame.startDate),
        adjustForInflation: cashFlowConfig.inflationConfig.inflationAdjusted,
        inflationRate: parseFloat(cashFlowConfig?.inflationConfig?.inflationPercent || '0'),
        account: cashFlowConfig.accountGUID || 'portfolio_level',
        repeatUntil: 'cancel',
    };

    // Check for occurrences
    if (cashFlowConfig.factorDateFrame.occurrenceCount) {
        result.repeatUntil = 'occurrences';
        result.repeatOccurrences = cashFlowConfig.factorDateFrame.occurrenceCount;
    }
    // Check for end date
    else if (cashFlowConfig.factorDateFrame.endDate) {
        result.repeatUntil = 'specific-date';
        result.repeatEndDate = convertServerDateToFormDate(cashFlowConfig.factorDateFrame.endDate);
    }

    return result;
};
